import React, { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../services/api';
import Swal from 'sweetalert2';
import { Icon } from '@iconify/react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';




import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import SupportTicketManager from '../components/SupportTicketManager';
import PatientManagement from '../components/PatientManagement';
import AppointmentManager from '../components/AppointmentManager';
import BillingManager from '../components/BillingManager';
import SubscriptionManager from '../components/SubscriptionManager';

const ClinicAdminDashboard = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [view, setView] = useState('dashboard'); // 'dashboard', 'doctors', 'staff', 'mr_visits', 'clinic_settings', 'settings'
    const [searchQuery, setSearchQuery] = useState(''); // Search state

    const [staffList, setStaffList] = useState([]);
    const [mrVisits, setMrVisits] = useState([]); // New
    const [analyticsData, setAnalyticsData] = useState(null); // New
    const [auditLogs, setAuditLogs] = useState([]); // New
    const [loading, setLoading] = useState(true);
    const [currentUser, setCurrentUser] = useState(null);

    // Layout State
    const [sidebarActive, setSidebarActive] = useState(false);
    const [mobileMenu, setMobileMenu] = useState(false);

    // Modal State (Unified)
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentUserId, setCurrentUserId] = useState(null);
    const [modalData, setModalData] = useState({
        name: '',
        email: '',
        phone: '',
        password: '',
        role: 'Doctor', // Default
        specialization: '',
        designation: '',
        experienceYears: '',
        availability: [] // Schedule
    });

    // Schedule Modal State
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [selectedDoctor, setSelectedDoctor] = useState(null);
    const [scheduleData, setScheduleData] = useState([]);

    // Profile Settings State
    const [profileForm, setProfileForm] = useState({
        name: '',
        email: '',
        phone: '',
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: ''
    });

    // Clinic Settings State
    const [clinicSettings, setClinicSettings] = useState({
        address: '',
        mapUrl: '',
        emergencyContactNumber: ''
    });

    const toggleSidebar = () => setSidebarActive(!sidebarActive);
    const mobileMenuControl = () => setMobileMenu(!mobileMenu);
    const closeSidebar = () => setMobileMenu(false);

    const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];


    useEffect(() => {
        fetchAnalytics(); // Global fetch for subscription banner status

        const queryParams = new URLSearchParams(location.search);
        const currentView = queryParams.get('view');

        if (currentView === 'settings') {
            setView('settings');
            fetchUserProfile();
        } else if (currentView === 'staff') {
            setView('staff');
            fetchStaff();
        } else if (currentView === 'doctors') {
            setView('doctors');
            fetchStaff();
        } else if (currentView === 'mr_visits') {
            setView('mr_visits');
            fetchMRVisits();
        } else if (currentView === 'clinic_settings') {
            setView('clinic_settings');
            fetchClinicSettings();
        } else if (currentView === 'support') {
            setView('support');
        } else if (currentView === 'patients') {
            setView('patients');
        } else if (currentView === 'appointments') {
            setView('appointments');
        } else if (currentView === 'billing') {
            setView('billing');
        } else if (currentView === 'subscription') {
            setView('subscription');
        } else {
            // Default View: Dashboard
            setView('dashboard');
            fetchAuditLogs();
        }
    }, [location.search]);

    const fetchClinicSettings = async () => {
        try {
            const response = await api.get('/clinic/settings');
            const data = response.data;
            setClinicSettings({
                address: data.address || '',
                mapUrl: data.mapUrl || '',
                emergencyContactNumber: data.emergencyContactNumber || ''
            });
        } catch (error) {
            console.error('Error fetching clinic settings:', error);
        }
    };

    const fetchAuditLogs = async () => {
        setLoading(true);
        try {
            const response = await api.get('/clinic/audit-logs');
            setAuditLogs(response.data);
        } catch (error) {
            console.error('Error fetching audit logs:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAnalytics = async () => {
        try {
            const response = await api.get('/clinic/analytics');
            setAnalyticsData(response.data);
        } catch (error) {
            console.error('Error fetching analytics:', error);
        }
    };

    const fetchMRVisits = async () => {
        setLoading(true);
        try {
            const response = await api.get('/clinic/mr-visits');
            setMrVisits(response.data);
        } catch (error) {
            console.error('Error fetching mr visits:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchStaff = async () => {
        setLoading(true);
        try {
            const response = await api.get('/clinic/staff');
            setStaffList(response.data);
        } catch (error) {
            console.error('Error fetching staff list:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchUserProfile = async () => {
        try {
            const response = await api.get('/clinic/profile');
            const data = response.data;
            setCurrentUser(data);
            setProfileForm(prev => ({
                ...prev,
                name: data.name,
                email: data.email,
                phone: data.phone || ''
            }));
        } catch (error) {
            console.error('Error fetching profile:', error);
        } finally {
            setLoading(false);
        }
    };

    // --- Modal Functions ---
    const handleInputChange = (e) => {
        setModalData({ ...modalData, [e.target.name]: e.target.value });
    };

    const openCreateModal = (roleType) => {
        setIsEditing(false);
        setCurrentUserId(null);
        setModalData({
            name: '',
            email: '',
            phone: '',
            password: '',
            role: roleType,
            specialization: '',
            designation: '',
            experienceYears: '',
            availability: []
        });
        setShowModal(true);
    };

    const openEditModal = (user) => {
        setIsEditing(true);
        setCurrentUserId(user._id);
        setModalData({
            name: user.name,
            email: user.email,
            phone: user.phone || '',
            password: '', // Reset
            role: user.role,
            specialization: user.specialization || '',
            designation: user.designation || '',
            experienceYears: user.experienceYears || ''
        });
        setShowModal(true);
    };

    const handleModalSubmit = async (e) => {
        e.preventDefault();

        try {
            if (isEditing) {
                // UPDATE logic
                await api.put(`/clinic/staff/${currentUserId}`, modalData);
                Swal.fire('Success', 'Updated successfully', 'success');
            } else {
                // CREATE logic
                await api.post('/clinic/create-staff', modalData);
                Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: `${modalData.role} created and email sent!`,
                    timer: 2000,
                    showConfirmButton: false
                });
            }
            setShowModal(false);
            fetchStaff();
        } catch (error) {
            Swal.fire('Error', error.response?.data?.message || 'Operation failed', 'error');
        }
    };

    const handleDeleteUser = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "This action cannot be undone!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                await api.delete(`/clinic/staff/${id}`);
                Swal.fire('Deleted!', 'Staff record removed.', 'success');
                fetchStaff();
            } catch (error) {
                Swal.fire('Error', 'Failed to delete user', 'error');
            }
        }
    };

    // --- Schedule Functions ---
    const openScheduleModal = (doctor) => {
        setSelectedDoctor(doctor);
        // Initialize schedule data based on doctor's saved availability or defaults
        const existingSchedule = doctor.availability || [];
        const initializedSchedule = DAYS_OF_WEEK.map(day => {
            const found = existingSchedule.find(s => s.day === day);
            return found ? found : { day, startTime: '09:00', endTime: '17:00', isAvailable: false };
        });
        setScheduleData(initializedSchedule);
        setShowScheduleModal(true);
    };

    const handleScheduleChange = (index, field, value) => {
        const updatedSchedule = [...scheduleData];
        updatedSchedule[index][field] = value;
        setScheduleData(updatedSchedule);
    };

    const handleUpdateSchedule = async () => {
        try {
            await api.put(`/clinic/doctors/${selectedDoctor._id}/schedule`, { availability: scheduleData });
            Swal.fire('Success', 'Schedule updated successfully', 'success');
            setShowScheduleModal(false);
            fetchStaff();
        } catch (error) {
            Swal.fire('Error', 'Failed to update schedule', 'error');
        }
    };


    // --- Profile Settings ---
    const handleProfileChange = (e) => {
        setProfileForm({ ...profileForm, [e.target.name]: e.target.value });
    };

    const handleSaveProfile = async () => {
        if (profileForm.newPassword && profileForm.newPassword !== profileForm.confirmNewPassword) {
            Swal.fire('Error', 'New passwords do not match', 'error');
            return;
        }
        try {
            const data = {
                name: profileForm.name,
                phone: profileForm.phone
            };
            if (profileForm.newPassword) {
                data.password = profileForm.newPassword;
            }

            const res = await api.put('/users/profile', data);
            Swal.fire('Success', 'Profile updated successfully', 'success');
            setCurrentUser(res.data.user);
            setProfileForm(prev => ({ ...prev, currentPassword: '', newPassword: '' }));
        } catch (error) {
            Swal.fire('Error', error.response?.data?.message || 'Update failed', 'error');
        }
    };

    // Filter Logic
    const filteredList = staffList.filter(user => {
        const matchesRole = view === 'doctors' ? user.role === 'Doctor' : user.role === 'Assistant';
        const matchesSearch = user.name.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesRole && matchesSearch;
    });

    return (
        <section className={mobileMenu ? "overlay active" : "overlay"}>
            <Sidebar isSidebarActive={sidebarActive} mobileMenu={mobileMenu} closeSidebar={closeSidebar} />

            <main className={sidebarActive ? "dashboard-main active" : "dashboard-main"}>
                <Header toggleSidebar={toggleSidebar} mobileMenuControl={mobileMenuControl} />

                <div className="dashboard-main-body">
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24">
                        <h6 className="fw-semibold mb-0">Clinic Administration</h6>
                        <ul className="d-flex align-items-center gap-2">
                            <li className="fw-medium">Dashboard</li>
                            <li>-</li>
                            <li className="fw-medium text-primary-600">
                                {view === 'doctors' ? 'Doctors' : view === 'staff' ? 'Staff Members' : 'Settings'}
                            </li>
                        </ul>
                    </div>

                    {/* Subscription Status Alert */}
                    {analyticsData?.subscription && !analyticsData.subscription.isSubscriptionActive && view !== 'subscription' && (
                        <div className="alert alert-danger radius-12 p-24 mb-24 d-flex align-items-center justify-content-between animation-fade-in border-0 shadow-sm" style={{ backgroundColor: '#fff5f5', borderLeft: '5px solid #ff4d4f' }}>
                            <div className="d-flex align-items-center gap-3">
                                <div className="w-50-px h-50-px radius-circle bg-danger-100 text-danger-600 d-flex align-items-center justify-content-center text-2xl">
                                    <Icon icon="solar:danger-bold" />
                                </div>
                                <div>
                                    <h6 className="mb-1 text-danger-700 fw-bold">Subscription Action Required</h6>
                                    <p className="mb-0 text-secondary-700 text-sm">
                                        Your clinic subscription is <strong>{analyticsData.subscription.status === 'Active' ? 'Expired' : analyticsData.subscription.status}</strong>.
                                        Entity creation and team access are restricted until renewal.
                                    </p>
                                </div>
                            </div>
                            <button onClick={() => navigate('/clinic-admin?view=subscription')} className="btn btn-danger-600 radius-8 px-24 py-11 d-flex align-items-center gap-2 shadow-sm pulse-animation">
                                <Icon icon="solar:card-send-bold" className="text-xl" />
                                Renew Subscription
                            </button>
                        </div>
                    )}


                    {/* --- DASHBOARD (ANALYTICS & SUMMARY) --- */}
                    {view === 'dashboard' && (
                        <>
                            {/* Stats Rows */}
                            <div className="row mb-24">
                                <div className="col-xxl-12">
                                    <div className="row g-3 mb-4">
                                        <div className="col-md-3">
                                            <div className="card border-0 shadow-sm p-24 radius-12 d-flex flex-column align-items-center text-center gap-2 h-100 bounce-hover">
                                                <div className="w-50-px h-50-px radius-circle bg-primary-100 text-primary-600 d-flex align-items-center justify-content-center text-2xl mb-2">
                                                    <Icon icon="solar:users-group-rounded-bold" />
                                                </div>
                                                <div>
                                                    <p className="mb-4 text-secondary text-xs fw-bold text-uppercase tracking-wider">Total Doctors</p>
                                                    <h3 className="mb-0 fw-bold text-primary-700">{analyticsData?.totalDoctors || 0}</h3>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="card border-0 shadow-sm p-24 radius-12 d-flex flex-column align-items-center text-center gap-2 h-100 bounce-hover">
                                                <div className="w-50-px h-50-px radius-circle bg-info-100 text-info-600 d-flex align-items-center justify-content-center text-2xl mb-2">
                                                    <Icon icon="solar:calendar-mark-bold" />
                                                </div>
                                                <div>
                                                    <p className="mb-4 text-secondary text-xs fw-bold text-uppercase tracking-wider">Appointments Today</p>
                                                    <h3 className="mb-0 fw-bold text-info-700">{analyticsData?.todayAppointments || 0}</h3>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="card border-0 shadow-sm p-24 radius-12 d-flex flex-column align-items-center text-center gap-2 h-100 bounce-hover">
                                                <div className="w-50-px h-50-px radius-circle bg-success-100 text-success-600 d-flex align-items-center justify-content-center text-2xl mb-2">
                                                    <Icon icon="solar:check-circle-bold" />
                                                </div>
                                                <div>
                                                    <p className="mb-4 text-secondary text-xs fw-bold text-uppercase tracking-wider">Completed Today</p>
                                                    <h3 className="mb-0 fw-bold text-success-700">{analyticsData?.todayCompleted || 0}</h3>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="card border-0 shadow-sm p-24 radius-12 d-flex flex-column align-items-center text-center gap-2 h-100 bounce-hover">
                                                <div className="w-50-px h-50-px radius-circle bg-warning-100 text-warning-600 d-flex align-items-center justify-content-center text-2xl mb-2">
                                                    <Icon icon="solar:wallet-bold" />
                                                </div>
                                                <div>
                                                    <p className="mb-4 text-secondary text-xs fw-bold text-uppercase tracking-wider"> Total Transaction </p>
                                                    <h3 className="mb-0 fw-bold text-warning-700">₹{analyticsData?.totalRevenue || 0}</h3>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Quick Actions */}
                            <div className="d-flex flex-wrap gap-3 mb-24">
                                <button onClick={() => navigate('/clinic-admin?view=doctors')} className="btn btn-primary-600 radius-8 px-20 py-11 d-flex align-items-center gap-2">
                                    <Icon icon="solar:stethoscope-bold" className="text-xl" />
                                    Manage Doctors
                                </button>
                                <button onClick={() => navigate('/clinic-admin?view=staff')} className="btn btn-info-600 radius-8 px-20 py-11 d-flex align-items-center gap-2">
                                    <Icon icon="solar:user-id-bold" className="text-xl" />
                                    Manage Staff
                                </button>
                                <button onClick={() => openCreateModal('Doctor')} className="btn btn-outline-primary-600 radius-8 px-20 py-11 d-flex align-items-center gap-2">
                                    <Icon icon="ic:baseline-plus" className="text-xl" />
                                    Add New Doctor
                                </button>
                                <button onClick={() => openCreateModal('Assistant')} className="btn btn-outline-info-600 radius-8 px-20 py-11 d-flex align-items-center gap-2">
                                    <Icon icon="ic:baseline-plus" className="text-xl" />
                                    Add New Staff
                                </button>
                            </div>

                            {/* Recent Activities (Audit Logs) */}
                            <div className="card h-100 p-0 radius-12">
                                <div className="card-header border-bottom bg-base py-16 px-24">
                                    <h5 className="mb-0">Recent Activities</h5>
                                </div>
                                <div className="card-body p-24">
                                    <div className="table-responsive scroll-sm">
                                        <table className="table bordered-table sm-table mb-0">
                                            <thead>
                                                <tr>
                                                    <th>Action</th>
                                                    <th>Performed By</th>
                                                    <th>Details</th>
                                                    <th>Date & Time</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {auditLogs.length === 0 ? (
                                                    <tr><td colSpan="4" className="text-center">No recent activities found.</td></tr>
                                                ) : (
                                                    auditLogs.map(log => (
                                                        <tr key={log._id}>
                                                            <td><span className="fw-medium text-primary-600">{log.action}</span></td>
                                                            <td>{log.performedBy?.name || 'System'}</td>
                                                            <td>{log.details}</td>
                                                            <td>{new Date(log.timestamp).toLocaleString()}</td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {/* --- MR VISITS VIEW --- */}
                    {view === 'mr_visits' && (
                        <div className="card h-100 p-0 radius-12">
                            <div className="card-header border-bottom bg-base py-16 px-24">
                                <h5 className="mb-0">Medical Representative Visits</h5>
                            </div>
                            <div className="card-body p-24">
                                <div className="table-responsive scroll-sm">
                                    <table className="table bordered-table sm-table mb-0">
                                        <thead>
                                            <tr>
                                                <th>MR Name</th>
                                                <th>Company</th>
                                                <th>Doctor</th>
                                                <th>Check-In Time</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading ? <tr><td colSpan="5" className="text-center">Loading...</td></tr> :
                                                mrVisits.length === 0 ? <tr><td colSpan="5" className="text-center">No MR visits recorded.</td></tr> :
                                                    mrVisits.map(visit => (
                                                        <tr key={visit._id}>
                                                            <td><span className="fw-medium text-primary-600">{visit.mrName}</span></td>
                                                            <td>{visit.companyName}</td>
                                                            <td>{visit.doctorId?.name}</td>
                                                            <td>{new Date(visit.checkInTime).toLocaleString()}</td>
                                                            <td>
                                                                <span className={`px-24 py-4 radius-4 fw-medium text-sm ${visit.status === 'Completed' ? 'bg-success-focus text-success-600' : 'bg-warning-focus text-warning-600'}`}>
                                                                    {visit.status}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))
                                            }
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}


                    {/* --- CLINIC SETTINGS VIEW --- */}
                    {view === 'clinic_settings' && (
                        <div className="card h-100 p-0 radius-12">
                            <div className="card-header border-bottom bg-base py-16 px-24">
                                <h5 className="mb-0">Clinic Profile & Settings</h5>
                            </div>
                            <div className="card-body p-24">
                                <form onSubmit={async (e) => {
                                    e.preventDefault();
                                    try {
                                        await api.put('/clinic/settings', clinicSettings);
                                        Swal.fire('Success', 'Clinic settings updated successfully!', 'success');
                                    } catch (err) {
                                        Swal.fire('Error', 'Failed to update settings', 'error');
                                    }
                                }}>
                                    <div className="row">
                                        <div className="col-md-6 mb-20">
                                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">Emergency Contact</label>
                                            <input
                                                name="emergencyContactNumber"
                                                type="text"
                                                className="form-control radius-8"
                                                placeholder="Emergency Phone"
                                                value={clinicSettings.emergencyContactNumber}
                                                onChange={(e) => setClinicSettings({ ...clinicSettings, emergencyContactNumber: e.target.value })}
                                            />
                                        </div>
                                        <div className="col-md-12 mb-20">
                                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">Address</label>
                                            <textarea
                                                name="address"
                                                className="form-control radius-8"
                                                rows="2"
                                                placeholder="Clinic Address"
                                                value={clinicSettings.address}
                                                onChange={(e) => setClinicSettings({ ...clinicSettings, address: e.target.value })}
                                            ></textarea>
                                        </div>
                                        <div className="col-md-12 mb-20">
                                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">Map URL</label>
                                            <input
                                                name="mapUrl"
                                                type="text"
                                                className="form-control radius-8"
                                                placeholder="Google Maps Embed URL"
                                                value={clinicSettings.mapUrl}
                                                onChange={(e) => setClinicSettings({ ...clinicSettings, mapUrl: e.target.value })}
                                            />
                                        </div>
                                        <div className="col-12 text-end">
                                            <button type="submit" className="btn btn-primary-600 hover-text-white">Save Changes</button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}


                    {/* --- SUPPORT TICKETS VIEW --- */}
                    {view === 'support' && <SupportTicketManager />}


                    {/* --- PATIENT MANAGEMENT VIEW --- */}
                    {view === 'patients' && <PatientManagement />}


                    {/* --- APPOINTMENT MANAGEMENT VIEW --- */}
                    {view === 'appointments' && <AppointmentManager />}

                    {/* --- BILLING MANAGEMENT VIEW --- */}
                    {view === 'billing' && <BillingManager />}

                    {/* --- SUBSCRIPTION MANAGEMENT VIEW --- */}
                    {view === 'subscription' && <SubscriptionManager />}

                    {/* --- MY PROFILE SETTINGS --- */}
                    {view === 'settings' && (
                        <div className="card h-100 p-0 radius-12">
                            <div className="card-header border-bottom bg-base py-16 px-24">
                                <h5 className="mb-0">My Profile Settings</h5>
                            </div>
                            <div className="card-body p-24">
                                {/* Display Read-Only Important Info */}
                                <div className="p-3 mb-4 rounded-3 border bg-light">
                                    <h6 className="text-secondary mb-2">Account Information</h6>
                                    <div className="d-flex gap-5">
                                        <div><small className="d-block text-secondary">Role</small> <span className="fw-bold text-primary">{currentUser?.role}</span></div>
                                        <div><small className="d-block text-secondary">Joined</small> <span className="fw-bold">{new Date(currentUser?.createdAt).toLocaleDateString()}</span></div>
                                    </div>
                                </div>

                                <Form onSubmit={(e) => { e.preventDefault(); handleSaveProfile(); }}>
                                    <h6 className="text-primary-600 mb-3">Edit Details</h6>
                                    <Row><Col md={6}><Form.Group className="mb-3"><Form.Label>Name</Form.Label><Form.Control type="text" name="name" value={profileForm.name} onChange={handleProfileChange} /></Form.Group></Col>
                                        <Col md={6}><Form.Group className="mb-3"><Form.Label>Email</Form.Label><Form.Control type="email" value={profileForm.email} disabled className="bg-light" /></Form.Group></Col>
                                    </Row>
                                    <Row><Col md={6}><Form.Group className="mb-3"><Form.Label>Phone</Form.Label><Form.Control type="text" name="phone" value={profileForm.phone} onChange={handleProfileChange} /></Form.Group></Col></Row>

                                    <div className="d-flex justify-content-between align-items-center mb-3 mt-4">
                                        <div>
                                            <h5 className="mb-0 fw-bold text-dark">Clinic Settings</h5>
                                            <p className="text-sm text-secondary mb-0">Manage details and configurations</p>
                                        </div>
                                        <button className="btn btn-outline-warning btn-sm" onClick={async () => {
                                            try {
                                                const res = await api.post('/clinic/invoices/backfill', {});
                                                Swal.fire('Success', res.data.message, 'success');
                                            } catch (err) {
                                                Swal.fire('Error', 'Failed to backfill invoices', 'error');
                                            }
                                        }}>
                                            <Icon icon="solar:refresh-circle-bold" className="me-1" /> Backfill Invoices
                                        </button>
                                    </div>
                                    <hr className="my-4" />
                                    <h6 className="text-primary-600 mb-3">Change Password</h6>
                                    <Row>
                                        <Col md={4}><Form.Group className="mb-3"><Form.Label>Current Password</Form.Label><Form.Control type="password" name="currentPassword" value={profileForm.currentPassword} onChange={handleProfileChange} /></Form.Group></Col>
                                        <Col md={4}><Form.Group className="mb-3"><Form.Label>New Password</Form.Label><Form.Control type="password" name="newPassword" value={profileForm.newPassword} onChange={handleProfileChange} /></Form.Group></Col>
                                        <Col md={4}><Form.Group className="mb-3"><Form.Label>Confirm Password</Form.Label><Form.Control type="password" name="confirmNewPassword" value={profileForm.confirmNewPassword} onChange={handleProfileChange} /></Form.Group></Col>
                                    </Row>
                                    <div className="d-flex justify-content-end"><Button type="submit">Update Profile</Button></div>
                                </Form>
                            </div>
                        </div>
                    )}

                    {/* --- DOCTORS & STAFF LIST VIEW --- */}
                    {(view === 'doctors' || view === 'staff') && (
                        <div className="card h-100 p-0 radius-12">
                            <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
                                <h5 className="mb-0">{view === 'doctors' ? 'Manage Doctors' : 'Manage Staff (Assistants)'}</h5>
                                <div className="d-flex align-items-center gap-3">
                                    <Form.Control
                                        type="text"
                                        placeholder={`Search ${view === 'doctors' ? 'Doctors' : 'Staff'}...`}
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="form-control form-control-sm w-auto"
                                        style={{ minWidth: '200px' }}
                                    />
                                    <button
                                        onClick={() => openCreateModal(view === 'doctors' ? 'Doctor' : 'Assistant')}
                                        className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
                                    >
                                        <Icon icon="ic:baseline-plus" className="text-xl" />
                                        Add {view === 'doctors' ? 'Doctor' : 'Assistant'}
                                    </button>
                                </div>
                            </div>
                            <div className="card-body p-24">
                                <div className="table-responsive scroll-sm">
                                    <table className="table bordered-table sm-table mb-0">
                                        <thead>
                                            <tr>
                                                <th>Name / Designation</th>
                                                <th>Email</th>
                                                <th>Phone</th>
                                                {view === 'doctors' && <th>Specialization</th>}
                                                <th className="text-center">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading ? <tr><td colSpan="5" className="text-center">Loading...</td></tr> :
                                                filteredList.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="5" className="text-center">
                                                            {searchQuery ? `No matches found for "${searchQuery}"` : "No records found."}
                                                        </td>
                                                    </tr>
                                                ) :
                                                    filteredList.map(user => (
                                                        <tr key={user._id}>
                                                            <td>
                                                                <div className="d-flex align-items-center">
                                                                    <div className="w-40-px h-40-px rounded-circle flex-shrink-0 me-12 overflow-hidden bg-primary-50 d-flex justify-content-center align-items-center">
                                                                        <Icon icon={user.role === 'Doctor' ? "solar:stethoscope-bold" : "solar:user-id-bold"} className="text-primary-600 text-xl" />
                                                                    </div>
                                                                    <div>
                                                                        <span className="fw-medium d-block">{user.name}</span>
                                                                        {user.designation && <span className="text-xs text-secondary">{user.designation}</span>}
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td>{user.email}</td>
                                                            <td>{user.phone || 'N/A'}</td>
                                                            {view === 'doctors' && <td>{user.specialization || '-'}</td>}
                                                            <td className="text-center">
                                                                <div className="d-flex align-items-center gap-10 justify-content-center">
                                                                    {/* Doctor Schedule Button */}
                                                                    {user.role === 'Doctor' && (
                                                                        <button onClick={() => openScheduleModal(user)} title="Manage Schedule" className="bg-info-focus text-info-600 bg-hover-info-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle">
                                                                            <Icon icon="solar:calendar-date-bold" />
                                                                        </button>
                                                                    )}
                                                                    <button onClick={() => openEditModal(user)} className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle">
                                                                        <Icon icon="lucide:edit" />
                                                                    </button>
                                                                    <button onClick={() => handleDeleteUser(user._id)} className="bg-danger-focus text-danger-600 bg-hover-danger-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle">
                                                                        <Icon icon="fluent:delete-24-regular" />
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                            }
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* CREATE / EDIT MODAL */}
                    <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
                        <Modal.Header closeButton>
                            <Modal.Title>{isEditing ? 'Edit Details' : `Add New ${modalData.role}`}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <Form onSubmit={handleModalSubmit}>
                                <Row className="mb-3">
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>Full Name <span className="text-danger">*</span></Form.Label>
                                            <Form.Control type="text" name="name" value={modalData.name} onChange={handleInputChange} required placeholder="e.g. Dr. John Doe" />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>Phone Number</Form.Label>
                                            <Form.Control type="text" name="phone" value={modalData.phone} onChange={handleInputChange} />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                {/* Conditional Fields for Doctors */}
                                {modalData.role === 'Doctor' && (
                                    <>
                                        <Row className="mb-3">
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label>Designation <span className="text-danger">*</span></Form.Label>
                                                    <Form.Control type="text" name="designation" value={modalData.designation} onChange={handleInputChange} required placeholder="e.g. Senior Cardiologist" />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label>Specialization <span className="text-danger">*</span></Form.Label>
                                                    <Form.Control type="text" name="specialization" value={modalData.specialization} onChange={handleInputChange} required placeholder="e.g. Cardiology" />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        <Row className="mb-3">
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label>Experience (Years)</Form.Label>
                                                    <Form.Control type="number" name="experienceYears" value={modalData.experienceYears} onChange={handleInputChange} placeholder="e.g. 10" />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </>
                                )}

                                <Row className="mb-3">
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>Email Address <span className="text-danger">*</span></Form.Label>
                                            <Form.Control type="email" name="email" value={modalData.email} onChange={handleInputChange} required disabled={isEditing} />
                                            {!isEditing && <Form.Text className="text-muted">Login credentials will be sent here.</Form.Text>}
                                        </Form.Group>
                                    </Col>
                                    {!isEditing && (
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>Password <span className="text-danger">*</span></Form.Label>
                                                <Form.Control type="password" name="password" value={modalData.password} onChange={handleInputChange} required />
                                            </Form.Group>
                                        </Col>
                                    )}
                                </Row>

                                <div className="d-flex justify-content-end gap-3 mt-4">
                                    <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
                                    <Button variant="primary" type="submit">{isEditing ? 'Update' : 'Create'}</Button>
                                </div>
                            </Form>
                        </Modal.Body>
                    </Modal>

                    {/* MANAGE SCHEDULE MODAL */}
                    <Modal show={showScheduleModal} onHide={() => setShowScheduleModal(false)} centered size="lg">
                        <Modal.Header closeButton>
                            <Modal.Title>Manage Schedule - {selectedDoctor?.name}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <Form onSubmit={(e) => { e.preventDefault(); handleUpdateSchedule(); }}>
                                {scheduleData.map((slot, index) => (
                                    <Row key={slot.day} className="align-items-center mb-2">
                                        <Col xs={3}>
                                            <Form.Check
                                                type="checkbox"
                                                label={slot.day}
                                                checked={slot.isAvailable}
                                                onChange={(e) => handleScheduleChange(index, 'isAvailable', e.target.checked)}
                                                className="fw-medium"
                                            />
                                        </Col>
                                        <Col xs={4}>
                                            <Form.Control
                                                type="time"
                                                value={slot.startTime}
                                                onChange={(e) => handleScheduleChange(index, 'startTime', e.target.value)}
                                                disabled={!slot.isAvailable}
                                            />
                                        </Col>
                                        <Col xs={1} className="text-center">-</Col>
                                        <Col xs={4}>
                                            <Form.Control
                                                type="time"
                                                value={slot.endTime}
                                                onChange={(e) => handleScheduleChange(index, 'endTime', e.target.value)}
                                                disabled={!slot.isAvailable}
                                            />
                                        </Col>
                                    </Row>
                                ))}
                                <div className="d-flex justify-content-end gap-3 mt-4">
                                    <Button variant="secondary" onClick={() => setShowScheduleModal(false)}>Cancel</Button>
                                    <Button variant="primary" type="submit">Save Schedule</Button>
                                </div>
                            </Form>
                        </Modal.Body>
                    </Modal>

                </div>
            </main>
        </section>
    );
};

export default ClinicAdminDashboard;
